/**
 * ヤギプライム加入導線 Cookie（`_YAGISH_YAGIPRIME_ROUTE_ID`）の読み書き。Vue 非依存。
 *
 * 値は課金ダイアログを開いた機能名を `#` 区切りで積んだもの（例: `ai-chat#reason_long`）。
 * サーバーは加入時に末尾の要素を「加入導線」として記録する。
 *
 * - 同じ機能を続けて開いても積まない（ダイアログが複数マウントされている画面で二重に書かれるため）
 * - 直近 5 件だけ残す。以前は無制限に積んでいて Cookie が肥大化していた
 * - 加入済みと分かった時点で消す。残すと次の加入（解約→再加入）に古い導線が混ざる
 */

export const YAGIPRIME_ROUTE_COOKIE = '_YAGISH_YAGIPRIME_ROUTE_ID'

const MAX_SEGMENTS = 5
const SEPARATOR = '#'
/** 本番と開発でどちらでも読めるよう、既存実装と同じく両ドメインに書く */
const COOKIE_DOMAINS = ['yagish.jp', 'localhost']

/**
 * vue-cookies の必要最小限。expires/path に null を渡すため vue-cookies 同梱の型は使わない
 * （null の expires はセッション Cookie になる。省略すると既定の期限が付く）
 */
export type RouteCookieStore = {
  get: (key: string) => string | null | undefined
  set?: (
    key: string,
    value: string,
    expires: null,
    path: null,
    domain: string
  ) => unknown
  remove?: (key: string, path?: string | null, domain?: string) => unknown
}

export function appendRoute(
  chain: string | null | undefined,
  feature: string
): string {
  const current = chain || ''
  // JS の呼び出し元（YagiprimePaymentService）から文字列以外が来ても壊さない
  const next = typeof feature === 'string' ? feature.trim() : ''
  if (!next) return current

  const segments = current.split(SEPARATOR).filter((s) => s !== '')
  if (segments[segments.length - 1] === next) return current

  segments.push(next)
  return segments.slice(-MAX_SEGMENTS).join(SEPARATOR)
}

export function writeRouteCookie(
  cookies: RouteCookieStore,
  feature: string
): void {
  const value = appendRoute(cookies.get(YAGIPRIME_ROUTE_COOKIE), feature)
  if (!value) return
  for (const domain of COOKIE_DOMAINS) {
    cookies.set?.(YAGIPRIME_ROUTE_COOKIE, value, null, null, domain)
  }
}

/** vue-cookies の remove は domain を渡さないとドメイン Cookie を消せない */
export function clearRouteCookie(cookies: RouteCookieStore): void {
  for (const domain of COOKIE_DOMAINS) {
    cookies.remove?.(YAGIPRIME_ROUTE_COOKIE, null, domain)
  }
}
